const VIEW_MODEL_SOURCE = Symbol('viewModelSource');

type ViewModelHost = Record<string, any> & {
    [VIEW_MODEL_SOURCE]?: Record<string, any> | null;
};

const RESERVED_NAMES = new Set([
    'constructor',
    'ngOnInit',
    'ngOnDestroy',
    'ngOnChanges',
    'ngAfterViewInit',
    'ngAfterViewChecked',
    'ngAfterContentInit',
    'ngAfterContentChecked',
    'vm',
    'viewModel',
    'setViewModel',
    'bindViewModel'
]);

export function bindComponentViewModel(target: ViewModelHost, vm: Record<string, any> | null | undefined, extraReserved: string[] = []): void {
    target[VIEW_MODEL_SOURCE] = vm ?? null;

    if (!vm) {
        return;
    }

    const reserved = new Set([...RESERVED_NAMES, ...extraReserved]);
    const names = new Set<string>(Object.keys(vm));

    let prototype = Object.getPrototypeOf(vm);
    while (prototype && prototype !== Object.prototype) {
        for (const name of Object.getOwnPropertyNames(prototype)) {
            names.add(name);
        }
        prototype = Object.getPrototypeOf(prototype);
    }

    for (const name of names) {
        if (reserved.has(name) || name.startsWith('ɵ')) {
            continue;
        }

        // Delete any existing plain property so we can define a getter/setter proxy.
        // This allows components that declare `foo: any;` for TypeScript typing to still
        // receive the value from the view model via the proxy.
        try { delete target[name]; } catch { /* ignore */ }

        Object.defineProperty(target, name, {
            configurable: true,
            enumerable: false,
            get() {
                const source = target[VIEW_MODEL_SOURCE];
                const current = source?.[name];
                return typeof current === 'function' ? current.bind(source) : current;
            },
            set(value: unknown) {
                const source = target[VIEW_MODEL_SOURCE];
                if (source) {
                    source[name] = value;
                }
            }
        });
    }
}
